import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useCreateFeedback } from "../api/mutations";
import { CreateFeedbackRequest } from "../api/types";
import { GeneralFeedbackTypeEnum } from "../types";
import Button from "../componentLibrary/Button";
import Modal from "../componentLibrary/Modal";
import Combobox, { ComboboxOptionI } from "../componentLibrary/Combobox";
import Checkbox from "../componentLibrary/Checkbox";
import { useNotification } from "../hooks/useNotification";
import { usePageTitle } from "../hooks/usePageTitle";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  pageTitle?: string;
  lessonId?: string;
  moduleId?: string;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  pageTitle: propPageTitle,
  lessonId: propLessonId,
  moduleId: propModuleId,
}) => {
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackType, setFeedbackType] = useState<ComboboxOptionI>({
    value: GeneralFeedbackTypeEnum.PAGE_FEEDBACK,
    label: "Page Feedback",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canContact, setCanContact] = useState(false);
  const submittingRef = useRef(false);
  const location = useLocation();
  const { notify } = useNotification();

  const {
    pageTitle: hookPageTitle,
    lessonId: hookLessonId,
    moduleId: hookModuleId,
  } = usePageTitle();

  const currentPageTitle = propPageTitle || hookPageTitle;
  const lessonId = propLessonId || hookLessonId;
  const moduleId = propModuleId || hookModuleId;

  const getCurrentPageUrl = () => {
    return window.location.pathname + window.location.search;
  };
  const getPageNumber = () => {
    const windowUrl = new URLSearchParams(window.location.search);
    const pageFromWindow = windowUrl.get("page");

    const routerUrl = new URLSearchParams(location.search);
    const pageFromRouter = routerUrl.get("page");

    const urlString = window.location.href;
    const pageMatch = urlString.match(/[?&]page=(\d+)/);
    const pageFromMatch = pageMatch ? pageMatch[1] : null;

    const pageStr = pageFromWindow || pageFromRouter || pageFromMatch;
    return pageStr ? parseInt(pageStr, 10) + 1 : undefined;
  };

  const [pageNumber, setPageNumber] = useState<number | undefined>();

  useEffect(() => {
    if (isOpen) {
      const num = getPageNumber();
      setPageNumber(num);
      submittingRef.current = false;
    }
  }, [isOpen, location.search, location.pathname]);

  const createFeedbackMutation = useCreateFeedback({
    onSuccess: async () => {
      setFeedbackText("");
      setFeedbackType({
        value: GeneralFeedbackTypeEnum.PAGE_FEEDBACK,
        label: "Page Feedback",
      });
      setCanContact(false);
      setIsSubmitting(false);
      submittingRef.current = false;
      onClose();
      notify("success", "Thank you for your feedback!");
    },
    onError: () => {
      setIsSubmitting(false);
      submittingRef.current = false;
      notify("error", "Failed to submit feedback. Please try again.");
    },
  });

  const handleSubmit = async () => {
    if (submittingRef.current || !feedbackText.trim()) {
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);

    const feedbackData: CreateFeedbackRequest = {
      feedbackType: feedbackType.value as GeneralFeedbackTypeEnum,
      lessonId,
      moduleId,
      pageNumber,
      pageUrl: getCurrentPageUrl(),
      otherFeedback: feedbackText.trim(),
      canContact,
    };

    createFeedbackMutation.mutate(feedbackData);
  };

  const handleClose = () => {
    if (submittingRef.current) return;

    setFeedbackText("");
    setFeedbackType({
      value: GeneralFeedbackTypeEnum.PAGE_FEEDBACK,
      label: "Page Feedback",
    });
    setCanContact(false);
    onClose();
  };

  const hasContent = feedbackText.trim().length > 0;

  const feedbackTypeOptions: ComboboxOptionI[] = [
    { value: GeneralFeedbackTypeEnum.PAGE_FEEDBACK, label: "Page Feedback" },
    {
      value: GeneralFeedbackTypeEnum.GENERAL_FEEDBACK,
      label: "General Feedback",
    },
    { value: GeneralFeedbackTypeEnum.BUG_REPORT, label: "Bug Report" },
    {
      value: GeneralFeedbackTypeEnum.FEATURE_REQUEST,
      label: "Feature Request",
    },
  ];

  const showPageInfo =
    feedbackType.value === GeneralFeedbackTypeEnum.PAGE_FEEDBACK;

  const formatCurrentPageDisplay = () => {
    if (pageNumber !== undefined) {
      return `${currentPageTitle} - (Page ${pageNumber})`;
    }
    return currentPageTitle;
  };

  return (
    <Modal
      title="Give Feedback"
      size="xl"
      height="90vh"
      isOpen={isOpen}
      isCloseButtonVisible={false}
      onCloseModal={handleClose}
    >
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-8 md:p-10 lg:p-12 pb-4">
          <div className="mb-6">
            <p className="text-gray-700 mb-6">
              We&apos;d love to hear your thoughts! Your feedback helps us
              improve the experience for all users.
            </p>
          </div>

          <div className="space-y-6">
            <div>
              <Combobox
                label="Feedback Type"
                options={feedbackTypeOptions}
                value={feedbackType}
                onChange={setFeedbackType}
                placeholder="Select feedback type..."
                disabled={isSubmitting}
              />

              {showPageInfo && (
                <div className="mt-4 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                  <div>
                    <strong>Current page:</strong> {formatCurrentPageDisplay()}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label
                htmlFor="feedback"
                className="block text-lg font-medium text-gray-700 mb-3"
              >
                Your Feedback
              </label>
              <textarea
                id="feedback"
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Please share your thoughts, suggestions, or report any issues..."
                className="w-full h-64 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                disabled={isSubmitting}
                maxLength={2000}
              />
              <div className="mt-2 text-sm text-gray-500 mb-4">
                {feedbackText.length}/2000 characters
              </div>
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 bg-white border-t border-gray-200 p-4">
          <div className="flex flex-col gap-3">
            <div className="flex justify-center">
              <Checkbox
                label="I would like to be contacted about this feedback."
                checked={canContact}
                onChange={(e) => setCanContact(e.target.checked)}
                disabled={isSubmitting}
              />
            </div>
            <div className="flex flex-row gap-2 justify-center">
              {hasContent ? (
                <>
                  <Button
                    className="px-6 py-2"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Feedback"}
                  </Button>
                  <Button
                    className="px-6 py-2"
                    variant="secondary"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  className="px-8 py-2"
                  variant="secondary"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Close
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default FeedbackModal;
